import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Modal,
    Pressable,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import {
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    format,
    parse,
    parseISO,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
} from "date-fns";
import { RouteProp, useRoute } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Button, Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { TimeBasedService } from "../../../services/TimeBasedService";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";
type ReadingHistoryRouteProp = RouteProp<AuthStackParamList, "ReadingHistoryScreen">;

type EnumItem = {
    enumerationId: string;
    value: string;
    parameter?: string | number | null;
};

type WorshipHistoryApiRow = {
    data: {
        worshipId: string;
        userId: string;
        minutes: string;
        marks: number;
        worshipType: string;
        readingContent?: string | null;
        isEditable: boolean;
    };
    date: string;
};

type WorshipEditApiRow = {
    worshipId: string;
    minutes: string;
    date: string;
    marks: number;
    readingContent?: string | null;
};

const MIN_REQUIRED_CHARS = 10;
const CHARS_PER_BLOCK = 10;
const MARKS_PER_BLOCK = 5;

const timeOnlyToMinutes = (time: string | null | undefined) => {
    if (!time) return 0;

    const parts = time.split(":");
    const hours = parseInt(parts[0] || "0", 10);
    const minutes = parseInt(parts[1] || "0", 10);

    return hours * 60 + minutes;
};

const minutesToTimeOnlyString = (mins: number) => {
    const safe = Number.isFinite(mins) && mins >= 0 ? Math.floor(mins) : 0;
    const h = Math.floor(safe / 60);
    const m = safe % 60;

    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");

    return `${hh}:${mm}:00`;
};

const getEffectiveCharCount = (text: string) => {
    return (text || "").trim().length;
};

const calculateOtherSevaMarks = (text: string) => {
    const charCount = getEffectiveCharCount(text);

    if (charCount < MIN_REQUIRED_CHARS) return 0;

    const blocks = Math.floor(charCount / CHARS_PER_BLOCK);
    return MARKS_PER_BLOCK;
};

export function OtherSevaHistoryScreen() {
    const route = useRoute<ReadingHistoryRouteProp>();
    const { user } = useAuth();
    const today = useMemo(() => new Date(), []);
    const initialRange = resolveHistoryRange(route.params, today);

    const [filterType, setFilterType] = useState<FilterType>(
        initialRange.hasNavigationDates ? "Custom" : "Monthly"
    );

    const effectiveUserId = useMemo(() => {
        return initialRange.devoteeId || user?.userId || "";
    }, [initialRange.devoteeId, user?.userId]);

    const [fromDate, setFromDate] = useState(initialRange.fromDate);
    const [toDate, setToDate] = useState(initialRange.toDate);

    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [entries, setEntries] = useState<WorshipHistoryApiRow[]>([]);
    const [summary, setSummary] = useState({ isSelf: true });

    const [worshipTypeId, setWorshipTypeId] = useState<string | null>(null);

    const [editing, setEditing] = useState<WorshipEditApiRow | null>(null);
    const [editReadingContent, setEditReadingContent] = useState("");
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const editEffectiveCharCount = useMemo(() => {
        return getEffectiveCharCount(editReadingContent);
    }, [editReadingContent]);

    const editCalculatedMarks = useMemo(() => {
        return calculateOtherSevaMarks(editReadingContent);
    }, [editReadingContent]);

    const isEditValid = editEffectiveCharCount >= MIN_REQUIRED_CHARS && editCalculatedMarks >= 5;

    const applyFilter = (type: FilterType) => {
        setFilterType(type);
        if (type === "Custom") return;

        const now = new Date();
        let start = startOfMonth(now);
        let end = endOfMonth(now);

        if (type === "Weekly") {
            start = startOfWeek(now);
            end = endOfWeek(now);
        } else if (type === "Quarterly") {
            start = startOfQuarter(now);
            end = endOfQuarter(now);
        } else if (type === "Yearly") {
            start = startOfYear(now);
            end = endOfYear(now);
        }

        setFromDate(format(start, "yyyy-MM-dd"));
        setToDate(format(end, "yyyy-MM-dd"));
    };

    const openPicker = (mode: "from" | "to") => {
        const value = new Date(mode === "from" ? fromDate : toDate);
        DateTimePickerAndroid.open({
            value,
            mode: "date",
            onChange: (event, selectedDate) => {
                if (event.type !== "set" || !selectedDate) return;
                const v = format(selectedDate, "yyyy-MM-dd");
                if (mode === "from") setFromDate(v);
                else setToDate(v);
                setFilterType("Custom");
            },
        });
    };

    const loadEnumerationAndSetReading = useCallback(async () => {
        try {
            setLoading(true);

            const res = await TimeBasedService.getEnumeration("WorshipType");
            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load worship types");
                return;
            }

            const list = (res?.data || []) as EnumItem[];
            const other = list.find((x) => (x.value || "").trim().toLowerCase() === "other");

            if (!other?.enumerationId) {
                Flash.error("Other worship type not found in Enumeration");
                return;
            }

            setWorshipTypeId(other.enumerationId);
        } catch (e: any) {
            Flash.error(e?.message || "Enumeration load error");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadHistory = useCallback(async () => {
        try {
            if (!user?.userId) return;
            if (!worshipTypeId) return;
            if (!effectiveUserId) return;

            setLoading(true);

            const payload = { userId: effectiveUserId, fromDate, toDate, worshipType: worshipTypeId };
            const res = await TimeBasedService.getWorshipHistory(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load history");
                setEntries([]);
                return;
            }

            const list = (res?.data.records || []) as WorshipHistoryApiRow[];
            setSummary(res?.data.response);

            const normalized = list.map((row) => {
                return {
                    ...row,
                    data: {
                        ...row.data,
                        marks: calculateOtherSevaMarks(row.data.readingContent || ""),
                    },
                };
            });

            setEntries(normalized);
        } catch (e: any) {
            Flash.error(e?.message || "History load error");
        } finally {
            setLoading(false);
        }
    }, [effectiveUserId, fromDate, toDate, user?.userId, worshipTypeId]);

    useEffect(() => {
        loadEnumerationAndSetReading();
    }, [loadEnumerationAndSetReading]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const onRefresh = async () => {
        setRefreshing(true);
        try {
            await loadHistory();
        } finally {
            setRefreshing(false);
        }
    };

    const openEdit = async (worshipId: string) => {
        try {
            setLoading(true);

            const res = await TimeBasedService.getWorship(worshipId);
            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load entry");
                return;
            }

            const list = (res?.data || []) as WorshipEditApiRow[];
            const row = list?.[0];

            if (!row?.worshipId) {
                Flash.error("Entry not found");
                return;
            }

            setEditing(row);
            setEditReadingContent(row.readingContent ?? "");
        } catch (e: any) {
            Flash.error(e?.message || "Failed to open edit");
        } finally {
            setLoading(false);
        }
    };

    const saveEdit = async () => {
        if (!editing) return;
        if (!user?.userId) {
            Flash.error("User not found");
            return;
        }
        if (!worshipTypeId) {
            Flash.error("Worship type not ready");
            return;
        }
        if (!isEditValid) {
            Flash.error("Minimum 10 characters required.");
            return;
        }

        try {
            setLoading(true);

            let dateForSave = "";
            if (typeof editing.date === "string" && editing.date.length >= 10) {
                if (/^\d{4}-\d{2}-\d{2}/.test(editing.date)) {
                    dateForSave = editing.date.substring(0, 10);
                } else {
                    try {
                        dateForSave = format(parseISO(editing.date), "yyyy-MM-dd");
                    } catch {
                        dateForSave = today ? format(today, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
                    }
                }
            } else {
                dateForSave = format(new Date(), "yyyy-MM-dd");
            }

            const payload = {
                worshipId: editing.worshipId,
                userId: user.userId,
                date: dateForSave,
                worshipType: worshipTypeId,
                minutes: minutesToTimeOnlyString(1),
                marks: editCalculatedMarks,
                readingContent: editReadingContent?.trim() || null,
            };

            const res = await TimeBasedService.create(payload);
            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to update");
                return;
            }

            Flash.success("Success!", "Other Seva Updated Successfully!");
            setEditing(null);
            await loadHistory();
        } catch (e: any) {
            Flash.error(e?.message || "Update failed");
        } finally {
            setLoading(false);
        }
    };

    const deleteEntry = (worshipId: string) => {
        setPendingDeleteId(worshipId);
    };

    const Chip = ({ text, active, onPress }: { text: string; active: boolean; onPress: () => void }) => (
        <Pressable
            onPress={onPress}
            style={({ pressed }) => [
                styles.chip,
                active ? styles.chipActive : styles.chipInactive,
                pressed ? { opacity: 0.95 } : null,
            ]}
        >
            <Text style={[styles.chipText, { color: active ? "#fff" : "#1D4ED8" }]}>{text}</Text>
        </Pressable>
    );

    return (
        <DivineBackground overlayOpacity={0.95}>
            <Header title="History & Analytics" showBack showHome />

            <ScrollView
                contentContainerStyle={styles.page}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        colors={["#F97316"]}
                        tintColor="#F97316"
                    />
                }
            >
                <View style={styles.filterCard}>
                    <View style={styles.filterHead}>
                        <Text style={styles.filterIcon}>⛭</Text>
                        <Text style={styles.filterTitle}>Filter History</Text>
                    </View>

                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingBottom: 8 }}>
                        <Chip text="Weekly" active={filterType === "Weekly"} onPress={() => applyFilter("Weekly")} />
                        <Chip text="Monthly" active={filterType === "Monthly"} onPress={() => applyFilter("Monthly")} />
                        <Chip text="Quarterly" active={filterType === "Quarterly"} onPress={() => applyFilter("Quarterly")} />
                        <Chip text="Yearly" active={filterType === "Yearly"} onPress={() => applyFilter("Yearly")} />
                    </ScrollView>

                    <View style={styles.dateRow}>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.dateLabel}>From Date</Text>
                            <Pressable onPress={() => openPicker("from")} style={styles.dateBox}>
                                <Text style={styles.dateBoxIcon}>📅</Text>
                                <Text style={styles.dateBoxText}>{format(new Date(fromDate), "dd-MM-yyyy")}</Text>
                            </Pressable>
                        </View>

                        <View style={{ flex: 1 }}>
                            <Text style={styles.dateLabel}>To Date</Text>
                            <Pressable onPress={() => openPicker("to")} style={styles.dateBox}>
                                <Text style={styles.dateBoxIcon}>📅</Text>
                                <Text style={styles.dateBoxText}>{format(new Date(toDate), "dd-MM-yyyy")}</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>

                {loading && entries.length === 0 ? (
                    <View style={styles.emptyBox}>
                        <ActivityIndicator />
                        <Text style={styles.emptyText}>Loading...</Text>
                    </View>
                ) : entries.length === 0 ? (
                    <View style={styles.emptyBox}>
                        <Text style={styles.emptyText}>No records found for this period.</Text>
                    </View>
                ) : (
                    <View style={{ gap: 10 }}>
                        {entries.map((row) => {
                            const marks = calculateOtherSevaMarks(row.data.readingContent || "");

                            return (
                                <View key={row.data.worshipId} style={styles.itemRow}>
                                    <View style={{ flex: 1 }}>
                                        <Text style={styles.itemDate}>{row.date}</Text>
                                        <Text style={styles.itemDay}>
                                            {format(parse(row.date, "dd-MM-yyyy", new Date()), "EEEE")}
                                        </Text>
                                    </View>

                                    <View style={styles.rightStats}>
                                        <View style={{ width: 10 }} />
                                        <View style={{ alignItems: "center" }}>
                                            <Text style={styles.statValue}>{marks}</Text>
                                            <Text style={styles.statLabel}>Marks</Text>
                                        </View>
                                    </View>

                                    {summary.isSelf && (
                                        <View style={styles.actions}>
                                            {row.data.isEditable ? (
                                                <Pressable onPress={() => openEdit(row.data.worshipId)} style={styles.iconBtn} disabled={loading}>
                                                    <Text style={styles.iconBtnText}>✏️</Text>
                                                </Pressable>
                                            ) : (
                                                <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                                            )}
                                            <Pressable onPress={() => deleteEntry(row.data.worshipId)} style={[styles.iconBtn, styles.iconBtnDanger]} disabled={loading}>
                                                <Text style={[styles.iconBtnText, { color: "#DC2626" }]}>🗑️</Text>
                                            </Pressable>
                                        </View>
                                    )}

                                </View>
                            );
                        })}
                    </View>
                )}
            </ScrollView>

            <Modal transparent visible={!!editing} animationType="fade" onRequestClose={() => setEditing(null)}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalCard}>
                        <View style={styles.modalHead}>
                            <Text style={styles.modalTitle}>Edit Entry</Text>
                            <Pressable onPress={() => setEditing(null)} style={styles.modalClose}>
                                <Text style={{ fontSize: 16, fontWeight: "900" }}>✕</Text>
                            </Pressable>
                        </View>

                        {editing ? (
                            <Text style={styles.modalDate}>
                                Date:{" "}
                                {(() => {
                                    try {
                                        if (/^\d{4}-\d{2}-\d{2}/.test(editing.date)) {
                                            return format(parseISO(editing.date), "dd MMMM yyyy");
                                        }
                                        return format(parseISO(editing.date), "dd MMMM yyyy");
                                    } catch {
                                        return "-";
                                    }
                                })()}
                            </Text>
                        ) : null}

                        <Input
                            label="Other Seva Content"
                            value={editReadingContent}
                            onChangeText={(text) => {
                                if (text.length <= 1000) setEditReadingContent(text);
                            }}
                            placeholder="Describe your seva..."
                            multiline
                            numberOfLines={4}
                            textAlignVertical="top"
                            maxLength={1000}
                            inputStyle={{ height: 100 }}
                        />

                        {!isEditValid ? (
                            <Text style={styles.validationText}>
                                Minimum 10 characters required.
                            </Text>
                        ) : null}

                        <View style={styles.calcRow}>
                            <Text style={styles.calcLabel}>Calculated Marks:</Text>
                            <Text style={styles.calcValue}>{editCalculatedMarks}</Text>
                        </View>

                        <Text style={styles.charCount}>
                            {editEffectiveCharCount}/1000
                        </Text>

                        <View style={styles.modalBtns}>
                            <Pressable onPress={() => setEditing(null)} style={styles.modalCancel} disabled={loading}>
                                <Text style={styles.modalCancelText}>Cancel</Text>
                            </Pressable>

                            <View style={{ flex: 1 }}>
                                <Button
                                    title="Update"
                                    onPress={saveEdit}
                                    loading={loading}
                                    disabled={loading || !isEditValid}
                                />
                            </View>
                        </View>

                        {loading ? (
                            <View style={{ marginTop: 10, alignItems: "center" }}>
                                <ActivityIndicator />
                            </View>
                        ) : null}
                    </View>
                </View>
            </Modal>

            <ConfirmDialog
                visible={!!pendingDeleteId}
                title="Delete Entry"
                message="Are you sure you want to delete this entry?"
                confirmText="Delete"
                destructive
                loading={loading}
                onCancel={() => setPendingDeleteId(null)}
                onConfirm={async () => {
                    if (!pendingDeleteId) return;
                    try {
                        setLoading(true);
                        const res = await TimeBasedService.delete(pendingDeleteId);
                        if (res?.status !== 200) {
                            Flash.error(res?.message || "Failed to delete");
                            return;
                        }
                        Flash.success("Success!", "Entry Deleted Successfully!");
                        setPendingDeleteId(null);
                        await loadHistory();
                    } catch (e: any) {
                        Flash.error(e?.message || "Delete failed");
                    } finally {
                        setLoading(false);
                    }
                }}
            />
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    page: { padding: 14, paddingBottom: 40, gap: 12 },

    filterCard: {
        backgroundColor: "rgba(255,255,255,0.88)",
        borderRadius: 16,
        padding: 14,
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.16)",
        shadowColor: "#000",
        shadowOpacity: 0.10,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
        elevation: 3,
    },
    filterHead: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 },
    filterIcon: { fontSize: 16, color: "#2563EB" },
    filterTitle: { fontSize: 14, fontWeight: "900", color: "#1F2937" },

    chip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999 },
    chipActive: {
        backgroundColor: "#2563EB",
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 3 },
        elevation: 2,
    },
    chipInactive: { backgroundColor: "rgba(37,99,235,0.10)" },
    chipText: { fontSize: 12, fontWeight: "900" },

    dateRow: { flexDirection: "row", gap: 12, marginTop: 8 },
    dateLabel: { fontSize: 11, color: "rgba(17,24,39,0.55)", fontWeight: "800", marginLeft: 2, marginBottom: 6 },
    dateBox: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(243,244,246,0.85)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.08)",
        borderRadius: 12,
        paddingHorizontal: 10,
        paddingVertical: 10,
    },
    dateBoxIcon: { fontSize: 14, opacity: 0.6 },
    dateBoxText: { fontSize: 13, fontWeight: "800", color: "rgba(31,41,55,0.90)" },

    emptyBox: {
        backgroundColor: "rgba(255,255,255,0.55)",
        borderRadius: 16,
        paddingVertical: 28,
        alignItems: "center",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
    },
    emptyText: { marginTop: 8, color: "rgba(17,24,39,0.55)", fontWeight: "800" },

    itemRow: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: "rgba(255,255,255,0.92)",
        borderRadius: 14,
        padding: 14,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
    },
    itemDate: { fontSize: 14, fontWeight: "900", color: "rgba(17,24,39,0.85)" },
    itemDay: { fontSize: 11, marginTop: 4, color: "rgba(17,24,39,0.45)", fontWeight: "800" },

    rightStats: { flexDirection: "row", alignItems: "center", marginRight: 10 },
    statValue: { fontSize: 16, fontWeight: "900", color: "#1D4ED8" },
    statLabel: { marginTop: 2, fontSize: 10, fontWeight: "900", color: "rgba(0,0,0,0.35)" },

    actions: { flexDirection: "row", gap: 10 },
    iconBtn: {
        width: 36,
        height: 36,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(37,99,235,0.10)",
    },
    iconBtnDanger: { backgroundColor: "rgba(220,38,38,0.10)" },
    iconBtnPlaceholder: { opacity: 0 },
    iconBtnText: { fontSize: 14, fontWeight: "900", color: "#1D4ED8" },

    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.35)",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
    },
    modalCard: {
        width: "100%",
        maxWidth: 380,
        backgroundColor: "#fff",
        borderRadius: 18,
        padding: 16,
        shadowColor: "#000",
        shadowOpacity: 0.18,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 10 },
        elevation: 6,
    },
    modalHead: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
    modalTitle: { fontSize: 16, fontWeight: "900", color: "#111827" },
    modalClose: { width: 34, height: 34, borderRadius: 999, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.06)" },
    modalDate: { marginTop: 10, fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: "700" },

    validationText: {
        marginTop: 6,
        fontSize: 11,
        fontWeight: "800",
        color: "#DC2626",
    },
    ruleText: {
        marginTop: 6,
        fontSize: 11,
        fontWeight: "700",
        color: "rgba(17,24,39,0.50)",
    },
    calcRow: {
        marginTop: 10,
        borderRadius: 14,
        paddingHorizontal: 12,
        paddingVertical: 10,
        backgroundColor: "rgba(238,242,255,0.65)",
        borderWidth: 1,
        borderColor: "rgba(99,102,241,0.18)",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    calcLabel: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.65)" },
    calcValue: { fontSize: 20, fontWeight: "900", color: "#4F46E5" },

    charCount: {
        fontSize: 11,
        color: "rgba(0,0,0,0.45)",
        textAlign: "right",
        marginTop: 4,
    },

    modalBtns: { flexDirection: "row", gap: 10, marginTop: 16 },
    modalCancel: {
        flex: 1,
        height: 46,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.12)",
        backgroundColor: "rgba(255,255,255,0.95)",
        alignItems: "center",
        justifyContent: "center",
    },
    modalCancelText: { fontSize: 14, fontWeight: "900", color: "rgba(17,24,39,0.60)" },
});
